import { useCallback, useRef } from 'react';
import { UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { useZipCodeLookup } from './useZipCodeLookup';

interface UseAddressAutofillProps {
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  debounceMs?: number;
}

export const useAddressAutofill = ({
  setValue,
  getValues,
  debounceMs = 500,
}: UseAddressAutofillProps) => {
  const { lookupZipCode, isLookingUp } = useZipCodeLookup();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleZipCodeChange = useCallback(
    (zipCode: string) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer to perform lookup after debounce
      debounceTimerRef.current = setTimeout(async () => {
        // Allow 3+ characters (Canadian FSA is 3 chars, US ZIP is 5 chars)
        if (!zipCode || zipCode.replace(/\s+/g, '').length < 3) return;

        const result = await lookupZipCode(zipCode);

        if (result) {
          // Always update fields based on zip code
          if (result.city) {
            setValue('client_address.city', result.city);
          }
          if (result.state) {
            setValue('client_address.state', result.state);
          }
          if (result.country) {
            setValue('client_address.country', result.country);
          }
        }
      }, debounceMs);
    },
    [lookupZipCode, setValue, getValues, debounceMs]
  );

  return {
    handleZipCodeChange,
    isLookingUp,
  };
};
